import { FormControl, FormGroup } from '@angular/forms';
import type { Form, FormField } from '../interfaces/form.interface';
import { DropdownData, InputType } from '../interfaces/input.interface';
import { FormOperations } from '../utils/form-operations';
import { ReactiveFormDefaultValueResolver } from './reactive-form-default-value.resolver';
import { ReactiveFormValueUtils } from './reactive-form-value.utils';

export type ReactiveFormPatcherContext<T> = {
    form: Form<T>;
    model: FormGroup;
    previousData: T | null;
    dropdownsFinalized: boolean;
    formOperations: FormOperations<T>;
};

export class ReactiveFormModelPatcher<T> {
    private readonly defaultValueResolver = new ReactiveFormDefaultValueResolver<T>();

    public patchFromPreviousDataPristine(context: ReactiveFormPatcherContext<T>): void {
        this.forEachMutableField(context.form, (field) => {
            if (ReactiveFormValueUtils.isRepeatableField(field)) {
                return;
            }

            const control = this.getPristineControl(context.model, field.name ?? '');

            if (!control) {
                return;
            }

            const nextValue = this.defaultValueResolver.resolve(field, context);
            const isSingleDropdown = ReactiveFormValueUtils.isSingleDropdownField(field);

            const shouldKeepCurrentSingleDropdown = isSingleDropdown &&
                ReactiveFormValueUtils.hasNonEmptyValue(control.value) &&
                !ReactiveFormValueUtils.hasNonEmptyValue(nextValue);

            if (shouldKeepCurrentSingleDropdown) {
                return;
            }

            control.patchValue(nextValue, { emitEvent: false });
        });

        this.applyInitialSelectedValues(context);
    }

    public remapDropdownFieldsPristine(context: ReactiveFormPatcherContext<T>): void {
        this.forEachMutableField(context.form, (field) => {
            if (ReactiveFormValueUtils.isRepeatableField(field) || field.input?.type === InputType.TWO_FIELDS_CONCAT) {
                return;
            }

            const control = this.getPristineControl(context.model, field.name ?? '');

            if (!control) {
                return;
            }

            const primaryKey = field.input?.dropdownConfiguration?.valuePrimaryKey;

            if (!primaryKey) {
                return;
            }

            const isMulti = ReactiveFormValueUtils.isMultiSelectField(field);
            const dropdownData = field.input?.data as DropdownData<unknown> | undefined;
            const options = ReactiveFormValueUtils.getDropdownOptions(dropdownData);
            const optionsIndex = ReactiveFormValueUtils.createOptionsIndex(options, primaryKey);

            if (options.length === 0) {
                this.clearEmptyFinalizedDropdown(context, field, control, isMulti);
                return;
            }

            const currentValue = control.value;
            const nextValue = ReactiveFormValueUtils.remapValueWithOptions(
                currentValue,
                options,
                primaryKey,
                isMulti,
                context.dropdownsFinalized,
                optionsIndex,
            );

            if (ReactiveFormValueUtils.areValuesEquivalent(currentValue, nextValue)) {
                return;
            }

            control.patchValue(nextValue, { emitEvent: false });
            this.applySelected(context, field.name ?? '', nextValue);
        });
    }

    public applyInitialSelectedValues(context: ReactiveFormPatcherContext<T>): void {
        this.forEachMutableField(context.form, (field) => {
            if (ReactiveFormValueUtils.isRepeatableField(field)) {
                return;
            }

            const controlName = field.name ?? '';
            const control = context.model.get(controlName) as FormControl | null;

            if (!control) {
                return;
            }

            this.applySelected(context, controlName, control.value);
        });
    }

    private clearEmptyFinalizedDropdown(
        context: ReactiveFormPatcherContext<T>,
        field: FormField<T>,
        control: FormControl,
        isMulti: boolean,
    ): void {
        if (!context.dropdownsFinalized) {
            return;
        }

        const clearedValue: unknown = isMulti ? [] : null;

        if (ReactiveFormValueUtils.areValuesEquivalent(control.value, clearedValue)) {
            return;
        }

        control.patchValue(clearedValue, { emitEvent: false });
        this.applySelected(context, field.name ?? '', clearedValue);
    }

    private applySelected(context: ReactiveFormPatcherContext<T>, controlName: string, value: unknown): void {
        context.formOperations.applySelected(
            context.formOperations.getFieldByControlName(controlName),
            value as T,
        );
    }

    private getPristineControl(model: FormGroup, controlName: string): FormControl | null {
        if (!controlName) {
            return null;
        }

        const control = model.get(controlName) as FormControl | null;

        if (!control || !control.pristine) {
            return null;
        }

        return control;
    }

    private forEachMutableField(form: Form<T>, callback: (field: FormField<T>) => void): void {
        for (const section of form.sections) {
            if (!section.isVisible) {
                continue;
            }

            for (const field of section.fields) {
                if (!field.conditions.isVisible || field.conditions.isRetained) {
                    continue;
                }

                if (!field.name) {
                    continue;
                }

                callback(field);
            }
        }
    }
}
